import { type App, inject, type Ref } from 'vue'
import type { MeFragment } from '@/graphql/api'
import { assertNever, type CallBuffer } from '@/helpers'
import type {
    AnalyticsBrowser,
    EventProperties,
    ID,
    Options,
    SegmentEvent,
    Traits,
} from '@segment/analytics-next'
import { getDocument, getTimeout, getWindow } from '@/helpers/globals.ts'

type EventType = SegmentEvent['type'] | 'reset'
export type TrackBuffer = {
    type: EventType
    eventName?: string
    properties?: EventProperties
    traits?: Traits
    options?: Options
    userId?: ID
}
export type Tracked = TrackBuffer[]
declare global {
    interface Window {
        __test_tracked: Tracked
    }
}

export function installSegment(
    app: App,
    key: string | undefined,
    user: Ref<MeFragment | undefined | null>,
    disable: boolean
): Segment {
    const segment = createSegment(key, user, disable)

    app.provide(segmentKey, segment)

    return segment
}

function createSegment(
    key: string | undefined,
    user: Ref<MeFragment | undefined | null>,
    disable: boolean
): Segment {
    if (disable || import.meta.env.SSR || !key) {
        return createNoopSegment()
    }

    return createBufferedSegment(key, user)
}

function createBufferedSegment(
    key: string,
    user: Ref<MeFragment | undefined | null>
): Segment {
    const buffer: CallBuffer<Segment> = []
    let segment: Segment | null = null
    let isCreatingReal = false

    async function track(
        ...args: Parameters<AnalyticsBrowser['track']>
    ): Promise<void> {
        if (segment) {
            await segment.track(...args)
        } else {
            buffer.push(['track', args])
        }
    }

    async function login(me: MeFragment): Promise<void> {
        if (segment) {
            await segment.login(me)
        } else {
            buffer.push(['login', [me]])
        }
    }

    async function logout(me: MeFragment | null | undefined): Promise<void> {
        if (segment) {
            await segment.logout(me)
        } else {
            buffer.push(['logout', [me]])
        }
    }

    async function page(args: TrackPageArgs): Promise<void> {
        if (segment) {
            await segment.page(args)
        } else {
            buffer.push(['page', [args]])
        }
    }

    return {
        track,
        login,
        logout,
        page,
        async loadScripts(): Promise<void> {
            if (segment) {
                return
            }

            if (isCreatingReal) {
                return
            }

            isCreatingReal = true

            const seg = await createRealSegment(key, user)

            buffer.forEach(([method, args]) => {
                switch (method) {
                    case 'track':
                        seg.track(...args)
                        break
                    case 'login':
                        seg.login(...args)
                        break
                    case 'logout':
                        seg.logout(...args)
                        break
                    case 'page':
                        seg.page(...args)
                        break
                    case 'loadScripts':
                        break
                    default:
                        assertNever(method, undefined)
                }
            })

            segment = seg
        },
    }
}

async function createRealSegment(
    key: string,
    user: Ref<MeFragment | undefined | null>
): Promise<Segment> {
    const { AnalyticsBrowser } = await import('./segment-browser')
    const analytics: AnalyticsBrowser = AnalyticsBrowser.load({
        writeKey: key,
    })

    const window = getWindow()

    if (QA && window) {
        if (!window.__test_tracked) {
            window.__test_tracked = []
        }

        // !!! I M P O R T A N T !!!
        // DO NOT EVER FUCKING PUT A FUCKING AWAIT IN FRONT OF THIS SHIT
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢸⣿⡛⠛⠷⣶⣤⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⣿⡇⠀⠀⠀⠙⠻⣷⣄⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢠⣾⠟⠀⠀⠀⠀⠀⠀⠈⣹⣿⣆⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⡇⠀⠀⠀⠀⠀⣠⡴⠞⠉⠈⠻⣷⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣀⣤⣿⡧⠤⠤⠶⠖⠋⠉⠀⠀⠀⠀⠀⢹⣷⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣠⣾⠟⠉⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣰⣿⣦⣄⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⣾⠟⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣀⣴⠞⠉⠀⠉⠙⠻⣶⡄⠀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⢸⣿⠀⠀⠀⣀⣀⣀⠀⠀⠀⠀⢀⣠⡴⠞⠉⠀⢀⣀⣀⣀⠀⠀⠘⣿⡄⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⠀⠀⠀⢈⣿⣆⣴⠟⠉⠉⠉⠛⢶⡖⠛⠉⠁⠀⠀⢠⡾⠋⠉⠈⠉⠻⣦⣰⣿⣀⠀⠀⠀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⠀⢀⣤⣾⠟⠋⡿⠁⢀⣾⣿⣷⣄⠈⢿⡀⠀⠀⠀⢠⡟⠀⢠⣾⣿⣷⡄⠘⣿⠉⠛⢿⣦⡀⠀⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⢠⣿⠏⠀⠀⢸⡇⠀⢸⣿⣿⣿⣿⠀⢸⡇⠀⠀⠀⢸⡇⠀⣿⣿⣿⣿⣧⠀⢹⡇⠀⠀⠙⣿⡆⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⢸⡏⠀⠀⠀⠘⣧⠀⠘⣿⣿⣿⡟⠀⣸⠇⠀⣀⣤⢾⣇⠀⠹⣿⣿⣿⠇⠀⣾⠁⠀⠀⠀⢸⣿⠀⠀⠀⠀
        // ⠀⠀⠀⠀⠀⢸⣷⠀⠀⠀⠀⠹⣧⡀⠈⠉⠁⢀⣴⠿⠞⠋⠉⠀⠀⠻⣦⡀⠈⠉⠁⣠⡾⠃⠀⠀⠀⠀⣾⡏⠀⠀⠀⠀
        // ⠀⠀⢀⣠⣶⠿⠛⠛⠛⠛⠛⠛⠉⠙⠛⠒⠛⠋⠁⠀⠀⠀⠀⠀⠀⠀⠈⠙⠛⠚⠛⠉⠀⠀⠀⠀⢀⡼⠿⢷⣦⣄⠀⠀
        // ⠀⣠⡿⠋⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⢸⣟⠛⠛⠒⠶⠶⠶⠶⠶⠶⠶⠖⠚⠛⢛⡷⠀⠀⠀⣀⡴⠋⠀⠀⠀⠈⠻⣷⡄
        // ⢰⣿⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠙⢷⣄⡀⠀⠀⠀⠀⠀⠀⠀⢀⣠⡶⠛⢁⣠⡴⠞⠋⠀⠀⠀⠀⠀⠀⠀⠘⣿
        // ⢸⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠈⠙⠓⠶⠶⠦⠶⠶⣚⣫⡥⠶⠚⠋⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢠⣿
        // ⠈⢿⣆⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣀⣀⣤⡤⠴⠖⠚⠛⠉⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣀⣴⡿⠃
        // ⠀⠈⠛⣷⣦⣤⣤⣤⣤⣤⣤⣶⡶⠾⠿⠟⠿⠿⠿⠶⣶⣶⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣶⠶⠿⠛⠉⠀⠀
        analytics.addSourceMiddleware(({ payload, next }) => {
            const object = payload.obj

            const buffer: TrackBuffer = {
                type: object.type,
            }

            if (object.event) {
                buffer.eventName = object.event
            }

            if (object.properties) {
                buffer.properties = object.properties
            }

            if (object.traits) {
                buffer.traits = object.traits
            }

            if (object.userId) {
                buffer.userId = object.userId
            }

            if (object.options) {
                buffer.options = object.options
            }

            window.__test_tracked.push(buffer)

            return next(payload)
        })

        analytics.on('reset', () => {
            window.__test_tracked.push({
                type: 'reset',
            })
        })
    }

    async function track(
        ...args: Parameters<AnalyticsBrowser['track']>
    ): Promise<void> {
        if (user.value?.customer?.disabledThirdPartyCookies) {
            return
        }

        await analytics.track(...args)
    }

    async function login(me: MeFragment): Promise<void> {
        if (me.customer?.disabledThirdPartyCookies) {
            return
        }

        await Promise.all([
            analytics.identify(me.id, normalizeMeFragmentIdentify(me)),
            analytics.track('login', normalizeMeFragmentLoginLogout(me)),
        ])
    }

    async function logout(me: MeFragment | null | undefined): Promise<void> {
        const promises: Promise<any>[] = []

        if (me) {
            if (me.customer?.disabledThirdPartyCookies) {
                return
            }

            promises.push(
                analytics.track('logout', normalizeMeFragmentLoginLogout(me))
            )
        }

        promises.push(analytics.reset())

        await Promise.all(promises)
    }

    async function page(args: TrackPageArgs): Promise<void> {
        if (user.value?.customer?.disabledThirdPartyCookies) {
            return
        }

        await analytics.page(args)
    }

    return {
        track,
        login,
        logout,
        page,
        async loadScripts(): Promise<void> {},
    }
}

function createNoopSegment(): Segment {
    return {
        async track(): Promise<void> {},
        async login(): Promise<void> {},
        async logout(): Promise<void> {},
        async page(): Promise<void> {},
        async loadScripts(): Promise<void> {},
    }
}

const segmentKey = 'segment'

function normalizeMeFragmentIdentify(me: MeFragment) {
    return {
        name: `${me.firstName} ${me.lastName}`,
        email: me.email,
        phone: me.customer?.billingAddress?.phoneNumber?.e164,
    }
}

function normalizeMeFragmentLoginLogout(me: MeFragment) {
    return {
        user_id: me.id,
        ...normalizeMeFragmentIdentify(me),
    }
}

interface TrackPageArgs {
    url: string | undefined
    path: string | undefined
    title: string
}

interface Segment {
    track(...args: Parameters<AnalyticsBrowser['track']>): Promise<void>

    login(me: MeFragment): Promise<void>

    logout(me: MeFragment | null | undefined): Promise<void>

    page(page: TrackPageArgs): Promise<void>

    loadScripts(): Promise<void>
}

export function useSegment(): Segment {
    return inject(segmentKey) as Segment
}

export function useLoadSegmentOnInteractionOrDelay(delay: number): void {
    const { loadScripts } = useSegment()

    const timeout = getTimeout()
    const document = getDocument()

    if (!timeout || !document) {
        return
    }

    const { setTimeout, clearTimeout } = timeout

    const init = async (): Promise<void> => {
        await loadScripts()

        document.removeEventListener('scroll', init)
        document.removeEventListener('mousemove', init)
        document.removeEventListener('touchstart', init)
        clearTimeout(timeoutId)
    }

    const options: AddEventListenerOptions = {
        once: true,
    }

    document.addEventListener('scroll', init, options)
    document.addEventListener('mousemove', init, options)
    document.addEventListener('touchstart', init, options)

    const timeoutId = setTimeout(init, delay)
}
